import React, { useMemo } from "react";
import { computeLoadout, PLATE_PROFILES, fmt, kgToLbs } from "../lib/calc";

const COLORS_KG = {
  25: "#dc2626",
  20: "#2563eb",
  15: "#ca8a04",
  10: "#16a34a",
  5: "#e2e8f0",
  2.5: "#dc2626",
  1.25: "#9ca3af",
  0.5: "#e2e8f0",
  0.25: "#9ca3af",
};
const COLORS_LBS = {
  45: "#2563eb",
  35: "#ca8a04",
  25: "#16a34a",
  10: "#e2e8f0",
  5: "#dc2626",
  2.5: "#9ca3af",
};

const plateColor = (p, unit) =>
  (unit === "kg" ? COLORS_KG[p] : COLORS_LBS[p]) || "#374151";

const plateHeight = (p, unit) => {
  const max = unit === "kg" ? 25 : 45;
  return Math.min(34 + (p / max) * 100, 132);
};

const plateWidth = (p, unit) => {
  const big = unit === "kg" ? 10 : 25;
  if (p >= big) return 15;
  if (p >= (unit === "kg" ? 2.5 : 10)) return 10;
  return 6;
};

export const PlateLoader = ({ weight, unit, label, cfg, setCfg }) => {
  const { profileKey, bar, collars } = cfg;
  const profile = PLATE_PROFILES[profileKey];

  const setBar = (w) => setCfg({ ...cfg, bar: w });
  const setCollars = (fn) =>
    setCfg({ ...cfg, collars: typeof fn === "function" ? fn(collars) : fn });

  const switchProfile = (key) =>
    setCfg({
      profileKey: key,
      bar: PLATE_PROFILES[key].bars[0].w,
      collars: false,
    });

  const load = useMemo(
    () => computeLoadout({ target: weight, targetUnit: unit, profileKey, bar, collars }),
    [weight, unit, profileKey, bar, collars],
  );

  const pUnit = load.unit;
  const lbsRef = (v) => `${fmt(kgToLbs(v), 1)} lb`;

  return (
    <div className="tactical-card p-5 sm:p-6 animate-fade" data-testid="plate-loader">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="eyebrow">
            Chargement de la barre
            {label && (
              <span className="text-slate-500 normal-case tracking-normal">
                {" "}
                · {label}
              </span>
            )}
          </div>
          <h3 className="font-display text-xl font-extrabold uppercase text-white leading-none mt-1">
            Disques par côté
          </h3>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1.5">
            {profile.note}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Total chargé
          </div>
          <div className="font-mono text-white tabular text-lg font-bold">
            {fmt(load.total, 2)} {pUnit}
          </div>
          {pUnit === "kg" && (
            <div className="font-mono text-[10px] text-slate-500 tabular">
              ≈ {lbsRef(load.total)}
            </div>
          )}
        </div>
      </div>

      {/* Profils d'équipement */}
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        {Object.values(PLATE_PROFILES).map((p) => (
          <button
            key={p.key}
            data-testid={`plate-profile-${p.key}`}
            onClick={() => switchProfile(p.key)}
            className={`px-2 py-2 rounded-sm text-[10px] font-mono uppercase tracking-widest border transition ${
              profileKey === p.key
                ? "bg-[#cd383d] border-[#cd383d] text-white shadow-[0_0_14px_rgba(205,56,61,0.3)]"
                : "border-white/10 text-slate-400 hover:text-white hover:border-white/30"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select
          data-testid="plate-bar-select"
          value={bar}
          onChange={(e) => setBar(Number(e.target.value))}
          className="bg-[#12141a] border border-white/10 rounded-sm px-2.5 py-1.5 text-[11px] font-mono text-white focus:border-[#cd383d] outline-none"
        >
          {profile.bars.map((b) => (
            <option key={b.w} value={b.w}>
              {b.label}
            </option>
          ))}
        </select>
        <button
          data-testid="plate-collars-toggle"
          onClick={() => setCollars((c) => !c)}
          className={`px-2.5 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-widest border transition ${
            collars
              ? "border-[#cd383d]/60 text-white bg-[#cd383d]/10"
              : "border-white/10 text-slate-500 hover:text-white"
          }`}
        >
          {collars ? "Colliers inclus" : "Sans colliers"}
        </button>
        <span className="text-[10px] font-mono text-slate-600">
          {profile.collarLabel}
        </span>
      </div>

      <div className="flex items-center justify-center py-6 bg-black/30 rounded-sm border border-white/5 overflow-hidden">
        <div className="flex items-center">
          <div className="w-8 h-3 bg-neutral-500 rounded-l-sm" />
          {load.perSide.length === 0 ? (
            <div className="mx-4 text-xs font-mono text-slate-500 uppercase tracking-widest">
              Barre seule
            </div>
          ) : (
            load.perSide.flatMap(({ plate, count }) =>
              Array.from({ length: count }).map((_, i) => (
                <div
                  key={`${plate}-${i}`}
                  title={`${plate} ${pUnit}`}
                  className="mx-[1px] rounded-sm flex items-center justify-center text-[9px] font-black shadow-inner border border-black/40"
                  style={{
                    width: plateWidth(plate, pUnit),
                    height: plateHeight(plate, pUnit),
                    background: plateColor(plate, pUnit),
                    color: plate === 5 || plate === 10 ? "#111827" : "#ffffff",
                  }}
                >
                  <span
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    {plate}
                  </span>
                </div>
              )),
            )
          )}
          {collars && (
            <div
              title={`Collier ${profile.collar} ${pUnit}`}
              className="mx-[1px] w-2 rounded-sm bg-neutral-300 border border-black/40"
              style={{ height: 26 }}
            />
          )}
          <div className="w-24 h-1.5 bg-neutral-400" />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {load.perSide.map(({ plate, count }) => (
          <div
            key={plate}
            data-testid={`plate-row-${plate}`}
            className="flex items-center justify-between rounded-sm bg-black/25 border border-white/10 px-3 py-1.5"
          >
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">
              {plate} {pUnit}
              {pUnit === "kg" && (
                <span className="text-slate-600 normal-case tracking-normal">
                  {" "}
                  ({lbsRef(plate)})
                </span>
              )}
            </span>
            <span className="font-mono text-white tabular font-bold">× {count}</span>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap text-[11px] font-mono">
        <span className="text-slate-500">
          Visée :{" "}
          <span className="text-slate-300 tabular">
            {fmt(load.goal, 1)} {pUnit}
          </span>
        </span>
        {load.remainder > 0.01 && (
          <span className="text-amber-400/90">
            Écart / côté :{" "}
            <span className="text-white font-bold tabular">
              {fmt(load.remainder, 2)} {pUnit}
            </span>
          </span>
        )}
      </div>
    </div>
  );
};
